import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { FormattedDate } from 'react-intl';

import { TableSortIcon } from '~components/generic/table/TableSortIcon';
import { Job } from '~extension/web2print/model/Job';
import { JobActions } from '~extension/web2print/model/JobActions';
import { JobListClient } from '~extension/web2print/model/JobList';
import { PanelActions } from '~extension/web2print/view/JobList/PanelActions';

export type TableDataItemShape = {
  step: string;
  documentName: string;
  lastedit: string;
  actions: Job;
};

interface TableProps {
  columns: any;
  data: any;
}

function Table({ columns, data }: TableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'lastedit',
      desc: true,
    },
  ]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="table-frame">
        <table className="workspace-table table table-striped">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort() ? 'cursor-pointer select-none' : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <TableSortIcon type="asc" />,
                            desc: <TableSortIcon type="desc" />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function ClientJobsTable({ client, jobActions }: { client: JobListClient; jobActions: JobActions }) {
  const [data, setData] = useState<TableDataItemShape[]>([]);

  const columns = useMemo(
    () => [
      {
        header: client.name,
        accessorKey: client.name,
        enableSorting: false,
        columns: [
          {
            header: () => <span>Task</span>,
            accessorKey: 'step',
            cell: ({ getValue }) => (
              <span className={clsx('attribute-' + getValue().toLowerCase().replace(/\s/g, ''), 'workflow-status')}>
                {' '}
                {getValue()}{' '}
              </span>
            ),
          },
          {
            header: () => <span>Title</span>,
            accessorKey: 'documentName',
          },
          {
            header: () => <span>Last edited</span>,
            accessorKey: 'lastedit',
            cell: ({ getValue }) => <FormattedDate value={getValue()} year="numeric" month="long" day="numeric" />,
          },
          {
            header: () => <span></span>,
            accessorKey: 'actions',
            cell: ({ getValue }) => <PanelActions job={getValue()} jobActions={jobActions} />,
            enableSorting: false,
          },
        ],
      },
    ],
    [client.name, jobActions],
  );

  useEffect(() => {
    let items = [];

    if (client.jobs === undefined) return;

    client.jobs.forEach(job => {
      const item = {} as TableDataItemShape;
      item.step = job.step;
      item.documentName = job.documentName;
      item.lastedit = job.lastedit;
      item.actions = job;
      items.push(item);
    });

    setData(items);
  }, [client, jobActions]);

  return (
    <div>
      <Table columns={columns} data={data} />
    </div>
  );
}
